import { Injectable, Injector, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpEvent,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NotificationService } from '../services/notification.service';
import { Problems } from '@app/sales/constants/ProblemCodes';
import { diegoRoutes } from '../enums/orderflow-paths';

export class ProblemExistingCustomerSsn extends HttpErrorResponse {}
export class ProblemExistingCustomerIdAndSsnMismatch extends HttpErrorResponse {}
export class ProblemCustomerCreditCheck extends HttpErrorResponse {}

@Injectable({ providedIn: 'root' })
export class HttpErrorInterceptor implements HttpInterceptor {
  private injector = inject(Injector);
  private notificationService = inject(NotificationService);

  private router: Router;
  private reloadAttempts = 0;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error);
      })
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (this.router === undefined) {
      this.router = this.injector.get(Router);
    }
    switch (+error.status) {
      case HttpStatusCode.Unauthorized:
        this.router.navigate([diegoRoutes.login]);
        break;
      case HttpStatusCode.Forbidden:
        this.notificationService.error('Du har ikke rettigheter til å utføre handlingen. Kontakt forhandlersupport.');
        return throwError(error);
      case HttpStatusCode.Conflict:
        // Specific handling for known problems
        switch (error.error?.title) {
          case Problems.ExistingCustomerSsn:
            return throwError(new ProblemExistingCustomerSsn(error));
          case Problems.ExistingCustomerIdAndSsnMismatch:
            return throwError(new ProblemExistingCustomerIdAndSsnMismatch(error));
          case Problems.ExistingCustomerSsnAndLastnameMismatch:
          case Problems.NewCustomerInFlight:
          case Problems.MultipleCustomerCardsForSsn:
            return throwError(new ProblemCustomerCreditCheck(error));
        }

        // 409 usually indicates data change by a different user. Reloading the page should fetch new data
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';

        if (this.reloadAttempts > 0) {
          this.notificationService.error(
            'Noe gikk galt. Prøv på nytt eller kontakt forhandlersupport om problemet vedvarer.'
          );
          return throwError(error);
        }
        this.reloadAttempts++;

        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate([window.location.pathname]).then(() => {
            this.notificationService.error(
              'Noe gikk galt. Prøv på nytt eller kontakt forhandlersupport om problemet vedvarer.'
            );
          });
        });
        return throwError(error);
      default:
        return this.handleGenericError(error);
    }
  }

  private handleGenericError(response: HttpErrorResponse): Observable<never> {
    if (response.status === HttpStatusCode.BadRequest && response.error) {
      // If http response is problem detail
      if (response.error?.Errors && Object.keys(response.error?.Errors).length > 0) {
        // If Problem detail has error list or not
        let validationErrors = [];
        Object.values(response.error.Errors).forEach((error: string[]) => {
          if (error?.length > 0) {
            validationErrors.push(error);
          }
        });
        if (validationErrors.length > 0) {
          // Flatten error list into a single error message
          let error = new HttpErrorResponse({ ...response, error: validationErrors.join('\n') });
          throw error;
        }
      } else if (response.error.detail) {
        let error = new HttpErrorResponse({ ...response, error: response.error.detail });
        throw error;
      } else {
        // Set error message to empty string, so that it is not the default empty object
        let error = new HttpErrorResponse({ ...response, error: '' });
        throw error;
      }
    }
    return throwError(response);
  }
}
